/**
 * Subscription-entitlement helpers.
 *
 * Entitlements are a block of feature flags (plus a per-doctor monthly
 * `session_limit`) derived from the clinic's subscription plan. They arrive on
 * the token response's `user_context` and inside the JWT, and are persisted on
 * `storage.getAuthData().user.entitlements`.
 *
 * Resolution order for `hasEntitlement(feature)`:
 *   1. Super-admin sessions have NO entitlements and are treated as full access.
 *   2. Read the entitlements stored on the user (the normal path).
 *   3. Fall back to decoding the access-token JWT (covers users whose stored
 *      user_context predates entitlements but whose fresh JWT carries them).
 *   4. Default to `false` (feature locked).
 */
import { storage, Entitlements } from './storage';
import { decodeToken } from './jwtUtils';

/** Feature keys that gate UI surfaces. */
export type EntitlementFeature =
  | 'pre_consultation'
  | 'referral_letter_generation'
  | 'automated_workflows'
  | 'pdf_ingestion'
  | 'vbc_analytics'
  | 'chroniq'
  | 'patient_reported_outcomes'
  | 'multilingual_consult'
  | 'plato_integration'
  | 'care_gaps';

/** True when the current session is a super admin (full access, no entitlements). */
function isSuperAdmin(): boolean {
  return Boolean(storage.getAuthData()?.user?.is_super_admin);
}

/**
 * Resolve the entitlements block for the current session.
 * Prefers the stored user_context; falls back to the JWT payload.
 */
export function getEntitlements(): Entitlements | null {
  const stored = storage.getAuthData()?.user?.entitlements;
  if (stored && typeof stored === 'object') {
    return stored;
  }

  const token = storage.getAccessToken();
  if (token) {
    const decoded = decodeToken(token);
    if (decoded?.entitlements && typeof decoded.entitlements === 'object') {
      return decoded.entitlements;
    }
  }

  return null;
}

/**
 * Whether the current session is entitled to a feature.
 * Super-admins always pass. Missing/false entitlement → false.
 */
export function hasEntitlement(feature: EntitlementFeature | string): boolean {
  if (isSuperAdmin()) {
    return true;
  }

  const entitlements = getEntitlements();
  if (!entitlements) {
    return false;
  }

  return entitlements[feature] === true;
}

/**
 * Whether the current session's clinic has an active subscription.
 *
 * Drives the "subscription pending" dashboard block for medical officers of a
 * clinic that hasn't paid yet (invited-before-payment) or whose subscription
 * has lapsed. Only medical officers are ever blocked:
 *   - super-admins and clinic admins always pass (admins must reach billing to pay);
 *   - a missing `subscription_active` flag (token predates the feature) is
 *     treated as active, so a partial rollout never locks existing doctors out.
 * Only an explicit `subscription_active === false` blocks.
 */
export function isSubscriptionActive(): boolean {
  const user = storage.getAuthData()?.user;
  if (!user) {
    // No session — let the normal auth redirect handle it, don't show the block.
    return true;
  }
  // Admins / super-admins are never subscription-blocked.
  if (user.is_super_admin || user.is_admin) {
    return true;
  }
  // Only medical officers are gated.
  if (!user.is_medical_officer) {
    return true;
  }

  const entitlements = getEntitlements();
  // Default to active when the flag is absent (backward-compatible rollout).
  return entitlements?.subscription_active !== false;
}

/**
 * Per-doctor monthly session cap. `null` means unlimited (also returned for
 * super-admins). Returns `null` when no entitlements are available.
 */
export function getSessionLimit(): number | null {
  if (isSuperAdmin()) {
    return null;
  }

  const entitlements = getEntitlements();
  if (!entitlements) {
    return null;
  }

  const limit = entitlements.session_limit;
  return typeof limit === 'number' ? limit : null;
}
